// ShelfScreen — lifted from the MagicPatterns mockup (handoff 2026-06-25)
// and wired to the real localStorage shelf. Replaces the v0 Library.tsx
// as the /library route.
//
// Data sources changed (only):
//   - PROJECTS reads from localStorage `offbook.shelf.v1` (no Three Sisters
//     demo entry — that hardcode is removed per Ryan 2026-06-25)
//   - Tap-active-cover opens the project: fetches imported envelope, drops
//     it into arc state, navigates to /room (Rehearsal Room hub)
//   - Visual structure unchanged: leather covers, infinite-loop carousel,
//     swipe to step, meta travels with the active cover
import React, { useEffect, useRef, useState } from 'react';
import { motion } from 'framer-motion';
import { PlusIcon } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { OffBookMark } from '../components/OffBookLogo';
import { getImportedProject } from '../api/client';
import { useArc } from '../state/arc';
import { saveShelfEntry } from './Library';

// Plain touch handlers replace framer-motion drag for the carousel — drag
// was eating cover taps on iOS Safari (per Ryan iPhone test 2026-06-25).
// This matches the v0 Library pattern that's been working in production.

interface ShelfEntry {
  projectId: string;
  scriptName: string;
  character?: string | null;
  byline?: string;
  pullQuote?: string;
  // Optional generated cover URL (from cover-generation pipeline); when
  // absent the CSS leather-cover fallback paints the title page.
  coverImage?: string;
  lastVisited?: number;
}

const LOCAL_KEY = 'offbook.shelf.v1';
// Leather theme assigned deterministically by projectId hash.
const LEATHER_THEMES = ['leather-olive', 'leather-black', 'leather-navy'] as const;
function leatherFor(id: string): string {
  let h = 0;
  for (let i = 0; i < id.length; i++) h = (h << 5) - h + id.charCodeAt(i);
  return LEATHER_THEMES[Math.abs(h) % LEATHER_THEMES.length];
}

function loadShelf(): ShelfEntry[] {
  try {
    const raw = localStorage.getItem(LOCAL_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

const COVER_W = 172;
const COVER_H = 272;
const SPACING = 184;
const SWIPE_THRESHOLD = 40;

function wrappedOffset(i: number, active: number, n: number) {
  let offset = i - active;
  if (offset > n / 2) offset -= n;
  if (offset < -n / 2) offset += n;
  return offset;
}

export function ShelfScreen() {
  const navigate = useNavigate();
  const { update } = useArc();
  const [shelf, setShelf] = useState<ShelfEntry[]>([]);
  const [active, setActive] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  const [opening, setOpening] = useState(false);
  const [loadingId, setLoadingId] = useState<string | null>(null);

  useEffect(() => {
    setShelf(loadShelf());
  }, []);

  const N = shelf.length;
  const stepTo = (dir: number) => setActive((a) => (a + dir + N) % N);

  // Plain touch handlers — replaces framer-motion drag which was eating
  // tap-to-enter on iOS Safari.
  const touchStartX = useRef<number | null>(null);
  const touchStartT = useRef<number>(0);
  function onTouchStart(e: React.TouchEvent) {
    touchStartX.current = e.touches[0].clientX;
    touchStartT.current = performance.now();
    setIsDragging(false);
  }
  function onTouchMove() {
    // Once movement starts we lock isDragging so the cover-tap handler
    // knows to ignore the upcoming pointerup as a click.
    if (touchStartX.current !== null) setIsDragging(true);
  }
  function onTouchEnd(e: React.TouchEvent) {
    if (touchStartX.current === null) return;
    const dx = e.changedTouches[0].clientX - touchStartX.current;
    const dt = performance.now() - touchStartT.current;
    const v = Math.abs(dx) / Math.max(1, dt) * 1000;
    if (dx < -SWIPE_THRESHOLD || (dx < -10 && v > 300)) stepTo(1);
    else if (dx > SWIPE_THRESHOLD || (dx > 10 && v > 300)) stepTo(-1);
    touchStartX.current = null;
    // Reset isDragging on next tick so a click immediately after a quick
    // tap doesn't get stuck-suppressed.
    window.setTimeout(() => setIsDragging(false), 50);
  }

  async function openProject(entry: ShelfEntry) {
    setOpening(true);
    setLoadingId(entry.projectId);
    try {
      const env: any = await getImportedProject(entry.projectId);
      update({
        parsedProject: env.project,
        parsedScript: env.parsed_script ?? null,
        projectId: env.project_id ?? entry.projectId,
        selectedCharacter: entry.character ?? null,
        // Methodology stays null unless the deep link sets one — per
        // Ryan 2026-06-25, don't auto-default chubbuck (kills phantom
        // margin-question prompts that have no journal behind them).
      });
      // Bump lastVisited
      saveShelfEntry({
        projectId: env.project_id ?? entry.projectId,
        scriptName: entry.scriptName,
        character: entry.character ?? null,
        lastVisited: Date.now(),
      });
      navigate('/room');
    } catch (e: any) {
      alert(`Couldn't open — ${e?.message ?? 'unknown'}`);
      setOpening(false);
      setLoadingId(null);
    }
  }

  const spring = { type: 'spring', stiffness: 260, damping: 30 } as const;

  return (
    <div className="h-full w-full bg-cream-light relative overflow-hidden flex flex-col text-gray-900">
      {/* Ambient Presence — a faint pastel wash drifting behind the shelf */}
      <div className="presence-ambient" style={{ top: '8%', left: '50%', marginLeft: -210 }} />

      {/* Header */}
      <div className="w-full pt-14 px-7 flex justify-between items-center relative z-20 shrink-0">
        <h1 className="font-display text-gray-900 text-xl tracking-[0.2em] uppercase flex items-center gap-2.5">
          <OffBookMark size={15} />
          Library
        </h1>
        <button
          aria-label="Add script"
          onClick={() => navigate('/upload')}
          className="w-9 h-9 rounded-full border border-gray-900/12 flex items-center justify-center text-gray-700 active:bg-gray-900/5 transition-colors"
        >
          <PlusIcon className="w-4 h-4" />
        </button>
      </div>

      {/* Empty-state */}
      {N === 0 ? (
        <div className="flex-1 flex flex-col items-center justify-center text-center px-8">
          <p className="font-serif italic text-gray-500 text-lg max-w-[260px] leading-snug">
            Your shelf is empty. Tap an invite link or add a script to begin.
          </p>
        </div>
      ) : (
        <>
          {/* Cover carousel — plain touch handlers on the wrapper, simple
              onClick on each cover. The v0 pattern that's been working in
              prod — drops framer-motion drag because it was eating taps on
              iOS Safari (Ryan iPhone test 2026-06-25). */}
          <div
            className="flex-1 w-full relative flex items-center justify-center select-none"
            style={{ touchAction: 'pan-y' }}
            onTouchStart={onTouchStart}
            onTouchMove={onTouchMove}
            onTouchEnd={onTouchEnd}
          >
            <div className="relative w-full h-[300px]">
              {shelf.map((entry, i) => {
                const offset = wrappedOffset(i, active, N);
                const isActive = offset === 0;
                return (
                  <motion.div
                    key={entry.projectId}
                    role="button"
                    aria-label={isActive ? `Open ${entry.scriptName}` : entry.scriptName}
                    className={`absolute cursor-pointer ${entry.coverImage ? '' : `${leatherFor(entry.projectId)} leather-cover`}`}
                    style={{
                      width: COVER_W,
                      height: COVER_H,
                      left: '50%',
                      top: '50%',
                      marginLeft: -COVER_W / 2,
                      marginTop: -COVER_H / 2,
                      transformOrigin: 'center center',
                      zIndex: 20 - Math.abs(offset),
                    }}
                    animate={{
                      x: offset * SPACING,
                      scale: isActive ? 1 : 0.86,
                      opacity: Math.abs(offset) > 1 ? 0 : isActive ? 1 : 0.55,
                    }}
                    transition={spring}
                    onClick={() => {
                      if (isDragging) return;
                      if (isActive) openProject(entry);
                      else stepTo(offset > 0 ? 1 : -1);
                    }}
                  >
                    {entry.coverImage ? (
                      <img
                        src={entry.coverImage}
                        alt={`${entry.scriptName} cover`}
                        className="w-full h-full object-contain"
                        draggable={false}
                      />
                    ) : (
                      <>
                        {/* CSS fallback title page (used before a cover is generated) */}
                        <div className="leather-spine" />
                        <div className="manuscript-rule" />
                        <div className="brass-rivet top-[14%]" />
                        <div className="brass-rivet top-1/2 -translate-y-1/2" />
                        <div className="brass-rivet bottom-[14%]" />
                        <div className="absolute inset-0 z-20 flex flex-col items-center justify-center text-center pl-6 pr-3">
                          <h2 className="font-display leading-[0.92] tracking-wide gold-foil text-[30px] break-words">
                            {entry.scriptName.toUpperCase()}
                          </h2>
                          <div className="w-7 h-px bg-gradient-to-r from-transparent via-[#D4AF37] to-transparent my-2.5 opacity-70" />
                          {entry.byline && (
                            <p className="font-mono text-[8px] tracking-[0.22em] gold-foil opacity-90 leading-relaxed">
                              {entry.byline.toUpperCase()}
                            </p>
                          )}
                        </div>
                      </>
                    )}
                  </motion.div>
                );
              })}
            </div>
          </div>

          {/* Meta carousel — character + quote travels with active cover */}
          <div className="relative w-full h-[150px] shrink-0 overflow-hidden">
            {shelf.map((entry, i) => {
              const offset = wrappedOffset(i, active, N);
              const isActive = offset === 0;
              return (
                <motion.div
                  key={entry.projectId}
                  className="absolute inset-x-0 top-0 px-8 flex flex-col items-center text-center"
                  animate={{ x: offset * 320, opacity: isActive ? 1 : 0 }}
                  transition={spring}
                >
                  {entry.character && (
                    <span className="font-mono text-[9px] uppercase tracking-[0.3em] text-gray-400 mb-3">
                      As {entry.character}
                    </span>
                  )}
                  {entry.pullQuote && (
                    <p className="font-serif italic text-gray-600 text-lg leading-relaxed max-w-[260px]">
                      {entry.pullQuote}
                    </p>
                  )}
                  <span className="mt-6 font-mono text-[9px] uppercase tracking-[0.25em] text-gray-400">
                    {loadingId === entry.projectId ? 'Opening…' : 'Tap cover to enter'}
                  </span>
                </motion.div>
              );
            })}
          </div>
        </>
      )}

      {/* Opening transition */}
      {opening && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="absolute inset-0 bg-cream-light/90 backdrop-blur-md z-50 flex flex-col items-center justify-center gap-5"
        >
          <motion.div
            animate={{ scale: [1, 1.12, 1] }}
            transition={{ duration: 1.4, repeat: Infinity, ease: 'easeInOut' }}
            className="w-3 h-3 rounded-full dot-light"
          />
          <p className="font-serif italic text-gray-700 text-lg">Opening the Rehearsal Room…</p>
        </motion.div>
      )}
    </div>
  );
}
